'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import { LayoutDashboard, CalendarDays, Camera, AlertCircle, User, ClipboardList, PoundSterling, BarChart3, Users, Settings, Briefcase, ShieldCheck, X, Bell, MessageCircle, UserCheck, LifeBuoy } from 'lucide-react';

interface SidebarProps {
  role: 'customer' | 'cleaner' | 'admin';
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const customerNav = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/customer-dashboard' },
  { icon: CalendarDays, label: 'My Bookings', href: '/customer-dashboard' },
  { icon: Camera, label: 'Photos', href: '/customer-dashboard' },
  { icon: AlertCircle, label: 'Complaints', href: '/customer-dashboard' },
  { icon: MessageCircle, label: 'Live Chat', href: '/customer-dashboard' },
  { icon: Bell, label: 'Notifications', href: '/notifications' },
  { icon: LifeBuoy, label: 'Help Centre', href: '/help-centre' },
  { icon: User, label: 'Account', href: '/account' },
];

const cleanerNav = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/cleaner-dashboard' },
  { icon: ClipboardList, label: 'My Jobs', href: '/cleaner-jobs' },
  { icon: Camera, label: 'Photo Uploads', href: '/cleaner-photo-uploads' },
  { icon: PoundSterling, label: 'Earnings', href: '/cleaner-earnings' },
  { icon: Bell, label: 'Notifications', href: '/cleaner-notifications' },
  { icon: User, label: 'Profile', href: '/cleaner-account' },
];

const adminNav = [
  { icon: LayoutDashboard, label: 'Overview', href: '/admin-dashboard' },
  { icon: Briefcase, label: 'Jobs', href: '/admin-jobs' },
  { icon: Users, label: 'Cleaners', href: '/admin-cleaners' },
  { icon: UserCheck, label: 'Onboarding', href: '/admin-onboarding' },
  { icon: Users, label: 'Customers', href: '/admin-customers' },
  { icon: AlertCircle, label: 'Complaints', href: '/admin-complaints', badge: 3 },
  { icon: MessageCircle, label: 'Live Chat', href: '/admin-dashboard' },
  { icon: BarChart3, label: 'Reports', href: '/admin-reports' },
  { icon: Bell, label: 'Notifications', href: '/admin-notifications' },
  { icon: ShieldCheck, label: 'Settings', href: '/admin-settings' },
];

const navMap = { customer: customerNav, cleaner: cleanerNav, admin: adminNav };

const roleLabels = { customer: 'Customer Portal', cleaner: 'Cleaner Portal', admin: 'Admin Panel' };

export default function Sidebar({ role, collapsed, mobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const navItems = navMap[role];

  const sidebarClasses = `
    fixed lg:relative inset-y-0 left-0 z-40
    flex flex-col bg-card border-r border-border
    transition-all duration-300 ease-in-out
    ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    ${collapsed ? 'lg:w-16' : 'lg:w-60'}
    w-60
  `;

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
        <div className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-lg text-foreground hidden lg:block">EzCleanCare</span>
          )}
          <span className="font-bold text-lg text-foreground lg:hidden">EzCleanCare</span>
        </div>
        <button
          onClick={onMobileClose}
          className="ml-auto lg:hidden p-1 rounded-lg text-muted-foreground hover:bg-muted"
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
      </div>

      {/* Role label */}
      {!collapsed && (
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{roleLabels[role]}</p>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={`nav-${item.label}`}
              href={item.href}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150
                ${isActive
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
                ${collapsed ? 'lg:justify-center lg:px-0' : ''}
              `}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="hidden lg:block">{item.label}</span>}
              <span className="lg:hidden">{item.label}</span>
              {'badge' in item && item.badge && !collapsed && (
                <span className="ml-auto bg-danger text-primary-foreground text-xs font-bold px-1.5 py-0.5 rounded-full hidden lg:block">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className={`px-2 py-3 border-t border-border ${collapsed ? 'lg:px-1' : ''}`}>
        <Link
          href="/sign-up-login-screen"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
        >
          <Settings size={18} className="flex-shrink-0" />
          {!collapsed && <span className="hidden lg:block">Sign Out</span>}
          <span className="lg:hidden">Sign Out</span>
        </Link>
      </div>
    </aside>
  );
}